import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Loader2, ArrowLeft, ArrowRight, Upload, Clock } from "lucide-react";
import { LogoMark } from "@/components/logo/AetriaLogo";
import { formatApiErrorDetail } from "@/lib/api";
import { PALETTES, PRESETS, FALLBACK_BANDS, matchBand } from "@/lib/brandingOptions";
import { BandSlider, BandReadout, maxSubscribableCap } from "@/components/billing/BandSlider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_BACKEND_URL;
const STEP_KEYS = ["signup.stepOrganization", "signup.stepBranding", "signup.stepPlan", "signup.stepReview"];

export default function SignupPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sub, setSub] = useState({ value: "", available: null, reason: "", checking: false });
  const [form, setForm] = useState({
    org_name: "", admin_email: "", admin_password: "", admin_name: "",
    product_name: "", primary_color: "#1E293B", accent_color: "#D4A017",
    preset: "consulting", plan: "starter",
  });
  const [bands, setBands] = useState(FALLBACK_BANDS);
  const [count, setCount] = useState(15);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitted, setSubmitted] = useState(null); // {product_name, subdomain, message}
  const debounce = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    axios.get(`${API}/api/signup/pricing`)
      .then((r) => { if (r.data.bands?.length) setBands(r.data.bands); })
      .catch(() => {});
  }, []);
  const selBand = matchBand(bands, count);
  const maxCap = maxSubscribableCap(bands);
  const pickCount = (c) => {
    setCount(c);
    const b = matchBand(bands, c);
    if (b && !b.contact) setForm((f) => ({ ...f, plan: b.id }));
  };

  const checkSub = (value) => {
    const v = value.toLowerCase().trim();
    setSub({ value: v, available: null, reason: "", checking: !!v });
    clearTimeout(debounce.current);
    if (!v) return;
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/signup/check-subdomain`, { params: { subdomain: v } });
        setSub({ value: v, available: data.available, reason: data.reason, checking: false });
      } catch {
        setSub((s) => ({ ...s, checking: false }));
      }
    }, 400);
  };

  const pickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) { toast.error("Logo must be PNG, JPEG or WebP"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const step1Valid = form.org_name.length >= 2 && sub.available && /\S+@\S+\.\S+/.test(form.admin_email) && form.admin_password.length >= 8;

  const provision = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/signup/provision`, {
        org_name: form.org_name, subdomain: sub.value,
        admin_email: form.admin_email, admin_password: form.admin_password,
        admin_name: form.admin_name || undefined, plan: form.plan,
        branding: {
          product_name: form.product_name || form.org_name,
          primary_color: form.primary_color, accent_color: form.accent_color,
          preset: form.preset,
        },
      });
      setSubmitted({
        product_name: data.tenant.branding.product_name,
        subdomain: data.tenant.subdomain,
        admin_email: form.admin_email,
        message: data.message,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("signup.requestFailed"));
      setBusy(false);
    }
  };

  const Preview = () => (
    <div className="border border-slate-200 bg-white p-4" data-testid="signup-brand-preview">
      <p className="text-[9px] uppercase tracking-[0.18em] text-slate-400 mb-2">{t("signup.livePreview")}</p>
      <div className="flex">
        <div className="w-28 p-3" style={{ background: form.preset === "minimal" ? "#fff" : form.primary_color, border: form.preset === "minimal" ? "1px solid #e2e8f0" : "none" }}>
          <div className="flex items-center gap-1.5">
            {logoPreview
              ? <img src={logoPreview} alt="logo" className="h-5 w-auto max-w-[40px] object-contain" />
              : <div className="h-5 w-5 flex items-center justify-center text-[10px] font-bold text-white relative" style={{ background: form.primary_color }}>
                  {(form.product_name || form.org_name || "A")[0].toUpperCase()}
                  <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: form.accent_color }} />
                </div>}
            <span className="text-[8px] font-bold truncate" style={{ color: form.preset === "minimal" ? "#0f172a" : "#fff" }}>
              {(form.product_name || form.org_name || "Your OS").toUpperCase()}
            </span>
          </div>
          <div className="mt-3 space-y-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-1.5 w-full" style={{ background: form.preset === "minimal" ? "#e2e8f0" : "rgba(255,255,255,.15)", borderLeft: i === 0 ? `2px solid ${form.accent_color}` : "none" }} />
            ))}
          </div>
        </div>
        <div className="flex-1 p-3 bg-[#F8FAFC]">
          <div className="h-2 w-24" style={{ background: form.primary_color, borderRadius: form.preset === "modern" ? 4 : 0 }} />
          <div className="grid grid-cols-3 gap-1.5 mt-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white border border-slate-200 p-1.5" style={{ borderRadius: form.preset === "modern" ? 6 : 0 }}>
                <div className="h-1 w-6 bg-slate-200" />
                <div className="h-2 w-8 mt-1 font-bold" style={{ background: i === 1 ? form.accent_color : form.primary_color }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6" data-testid="signup-pending-screen">
        <div className="max-w-lg w-full bg-white border border-slate-200 p-10 text-center">
          <div className="flex justify-center mb-5"><LogoMark size={40} /></div>
          <div className="flex justify-center mb-4">
            <span className="h-12 w-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
              <Clock className="h-6 w-6 text-amber-500" />
            </span>
          </div>
          <h1 className="text-xl font-bold text-[#1E293B] tracking-tight" data-testid="signup-pending-title">
            {t("signup.requestReceivedTitle")}
          </h1>
          <p className="text-sm text-slate-500 mt-3 leading-relaxed" data-testid="signup-pending-message">
            {submitted.message}
          </p>
          <div className="mt-6 bg-slate-50 border border-slate-200 px-4 py-3 text-left">
            <p className="text-[11px] font-mono text-slate-500">
              <span className="text-slate-400">{t("signup.workspaceLabel")}</span> {submitted.product_name} · {submitted.subdomain}.aetriaos.com
            </p>
            <p className="text-[11px] font-mono text-slate-500 mt-1">
              <span className="text-slate-400">{t("signup.signInEmailLabel")}</span> {submitted.admin_email}
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            {t("signup.pendingNoteRequest")}
          </p>
          <Link to="/landing" data-testid="signup-pending-home"
            className="inline-block mt-7 text-[11px] font-semibold uppercase tracking-[0.12em] text-teal-700 hover:text-teal-800">
            {t("signup.backToHome")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="signup-page">
      <header className="bg-[#1E293B] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/landing" className="flex items-center gap-3">
            <LogoMark size={28} inverted />
            <span className="text-white font-bold text-[15px]">AETRIA<span className="text-teal-400 ml-1">OS</span></span>
          </Link>
          <span className="text-[11px] text-slate-400 uppercase tracking-[0.14em]">{t("signup.headerTaglineRequest")}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-8" data-testid="signup-steps">
          {STEP_KEYS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-6 w-6 flex items-center justify-center text-[11px] font-bold ${i <= step ? "brand-accent-bg text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</div>
              <span className={`text-[11px] uppercase tracking-wider ${i === step ? "text-[#1E293B] font-bold" : "text-slate-400"}`}>{t(s)}</span>
              {i < STEP_KEYS.length - 1 && <div className="w-8 h-px bg-slate-300" />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 p-8">
          {step === 0 && (
            <div className="space-y-4" data-testid="signup-step-org">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("signup.orgName")}</Label>
                <Input data-testid="signup-org-input" value={form.org_name} onChange={set("org_name")} placeholder="ToothMatrix Dental Group" className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("signup.chooseSubdomain")}</Label>
                <div className="flex items-center mt-1">
                  <Input data-testid="signup-subdomain-input" value={sub.value} onChange={(e) => checkSub(e.target.value)} placeholder="toothmatrix" className="rounded-none font-mono" />
                  <span className="text-[12px] text-slate-400 font-mono ms-2 whitespace-nowrap">.aetriaos.com</span>
                </div>
                {sub.value && (
                  <p className="text-[11px] mt-1.5 flex items-center gap-1" data-testid="subdomain-availability">
                    {sub.checking ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                      : sub.available ? <><Check className="h-3 w-3 text-teal-600" /><span className="text-teal-700">{t("signup.available")}</span></>
                      : <><X className="h-3 w-3 text-red-500" /><span className="text-red-600">{sub.reason || t("signup.unavailable")}</span></>}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                <div className="col-span-2"><Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("signup.adminAccount")}</Label></div>
                <Input data-testid="signup-admin-name-input" value={form.admin_name} onChange={set("admin_name")} placeholder={t("signup.yourName")} className="rounded-none" />
                <Input data-testid="signup-admin-email-input" type="email" value={form.admin_email} onChange={set("admin_email")} placeholder={t("signup.yourEmail")} className="rounded-none" />
                <div className="col-span-2">
                  <PasswordInput data-testid="signup-admin-password-input" value={form.admin_password} onChange={set("admin_password")} placeholder={t("signup.passwordMin")} className="rounded-none" />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="signup-step-branding">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("signup.productName")}</Label>
                <Input data-testid="signup-product-name-input" value={form.product_name} onChange={set("product_name")} placeholder={`${form.org_name || "Your"} OS`} className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("signup.logoLabel")}</Label>
                <div className="flex items-center gap-3 mt-1">
                  <label className="flex items-center gap-2 border border-slate-300 px-4 py-2 text-[12px] cursor-pointer hover:bg-slate-50">
                    <Upload className="h-3.5 w-3.5" /> {t("signup.chooseFile")}
                    <input data-testid="signup-logo-input" type="file" accept="image/png,image/jpeg,image/webp" onChange={pickLogo} className="hidden" />
                  </label>
                  {logoPreview && <img src={logoPreview} alt="logo preview" className="h-9 w-auto border border-slate-200 p-1" data-testid="signup-logo-preview" />}
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("signup.palette")}</Label>
                <div className="grid grid-cols-4 gap-2 mt-1.5">
                  {PALETTES.map((p) => (
                    <button key={p.name} data-testid={`palette-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setForm((f) => ({ ...f, primary_color: p.primary, accent_color: p.accent }))}
                      className={`border p-2 text-left hover:border-slate-400 ${form.primary_color === p.primary && form.accent_color === p.accent ? "border-[var(--brand-accent)] border-2" : "border-slate-200"}`}>
                      <div className="flex gap-1">
                        <div className="h-4 w-4" style={{ background: p.primary }} />
                        <div className="h-4 w-4" style={{ background: p.accent }} />
                      </div>
                      <p className="text-[9px] text-slate-500 mt-1 truncate">{p.name}</p>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <label className="flex items-center gap-2 text-[11px] text-slate-500">{t("signup.primary")}
                    <input data-testid="signup-primary-color" type="color" value={form.primary_color} onChange={set("primary_color")} className="h-7 w-10 border border-slate-300 cursor-pointer" />
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-slate-500">{t("signup.accent")}
                    <input data-testid="signup-accent-color" type="color" value={form.accent_color} onChange={set("accent_color")} className="h-7 w-10 border border-slate-300 cursor-pointer" />
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("signup.designPreset")}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {PRESETS.map((p) => (
                    <button key={p.id} data-testid={`preset-${p.id}`}
                      onClick={() => setForm((f) => ({ ...f, preset: p.id }))}
                      className={`border p-3 text-left hover:border-slate-400 ${form.preset === p.id ? "border-[var(--brand-accent)] border-2" : "border-slate-200"}`}>
                      <p className="text-[12px] font-bold text-[#1E293B]">{p.name}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{p.blurb}</p>
                    </button>
                  ))}
                </div>
              </div>
              <Preview />
            </div>
          )}

          {step === 2 && (
            <div data-testid="signup-step-plan">
              <BandSlider bands={bands} count={count} onChange={pickCount} testid="signup-band-slider" />
              <div className="mt-8">
                <BandReadout band={selBand} maxCap={maxCap} testid="signup-band-readout" />
              </div>
              {selBand?.contact ? (
                <p className="text-[12px] text-slate-600 mt-5 bg-amber-50 border border-amber-200 px-4 py-3"
                  data-testid="signup-contact-note">
                  {t("signup.contactSalesNote", { count: maxCap })}
                </p>
              ) : (
                <p className="text-[12px] text-slate-500 mt-5 bg-teal-50 border border-teal-200 px-4 py-3" data-testid="trial-note">
                  {t("signup.trialRequestNoteApproved")}
                </p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="signup-step-review">
              {[[t("signup.reviewOrganization"), form.org_name], [t("signup.reviewWorkspace"), `${sub.value}.aetriaos.com`],
                [t("signup.reviewAdmin"), `${form.admin_email}`], [t("signup.reviewProductName"), form.product_name || form.org_name],
                [t("signup.reviewPreset"), PRESETS.find((p) => p.id === form.preset)?.name],
                [t("signup.reviewPlan"), t("signup.reviewPlanValue", {
                  plan: bands.find((p) => p.id === form.plan)?.name,
                  price: `$${(bands.find((p) => p.id === form.plan)?.usd || 0).toLocaleString()}`,
                })]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 pb-2">
                  <span className="text-[11px] uppercase tracking-wider text-slate-400">{k}</span>
                  <span className="text-[13px] font-medium text-[#1E293B]">{v}</span>
                </div>
              ))}
              <Preview />
              <p className="text-[11px] text-slate-400">{t("signup.reviewNoteRequest")}</p>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-5 border-t border-slate-100">
            <Button variant="outline" data-testid="signup-back-btn" disabled={step === 0 || busy}
              onClick={() => setStep((s) => s - 1)} className="rounded-none">
              <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" /> {t("common.back")}
            </Button>
            {step < 3 ? (
              <Button data-testid="signup-next-btn"
                disabled={(step === 0 && !step1Valid) || (step === 2 && !!selBand?.contact)}
                onClick={() => setStep((s) => s + 1)} className="rounded-none bg-[#1E293B] hover:bg-slate-700 text-white">
                {t("common.continue")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
              </Button>
            ) : (
              <Button data-testid="signup-provision-btn" disabled={busy} onClick={provision}
                className="rounded-none brand-accent-bg hover:bg-teal-700 text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy ? t("signup.submitting") : t("signup.submitTrialRequest")}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
